import * as _ from 'lodash';

import { finleyAssert } from '@finley-technologies/common';

import { BaseCommitmentMechanicsFormData } from '../types';
import { CreditAgreementFullArtifact } from '~/core/routes/settings/implementations/common/types';

export const getFormValuesFromCommitment = (
  commitment: NonNullable<
    CreditAgreementFullArtifact['loanMechanicsV1']
  >['commitments'][number],
  creditAgreement: CreditAgreementFullArtifact,
): BaseCommitmentMechanicsFormData => {
  const result: BaseCommitmentMechanicsFormData = {
    lenders: [],
    products: [],
    servicingFees: [],
    totalCommitment: commitment.limit.amount,
    key: commitment.key,
    maturityDate: commitment.maturityDate,
    commitmentName: commitment.name,
  };

  // ** assert all lenders are the same across commitment loans. allows us to use loans[0]
  const defaultLenders = commitment.loans[0].lenders;
  finleyAssert(
    commitment.loans.every((l) => _.isEqual(defaultLenders, l.lenders)),
    `All lenders must be the same across all loans of a commitment [key: ${commitment.key}].`,
  );

  if (commitment.loans[0] !== undefined) {
    // Since every lenders array on every loan is identical, we can just grab the first one.
    result.lenders = commitment.loans[0].lenders.map((lender) => ({
      lenderId: lender.entity.id,
      lenderName: lender.entity.name,
      lenderSlug: lender.entity.slug,
      proRataAmount: lender.amount,
    }));

    const lendersWithServicingFees = commitment.loans[0].lenders.filter(
      (
        lender,
      ): lender is typeof lender & {
        // typeguard
        servicerFee: NonNullable<typeof lender.servicerFee>;
      } => lender.servicerFee !== undefined,
    );

    result.servicingFees = lendersWithServicingFees.map((lender) => ({
      lenderId: lender.entity.id,
      lenderName: lender.entity.name,
      lenderSlug: lender.entity.slug,
      servicingFee: lender.servicerFee.rate,
    }));
  }

  // The same data, just with different types, depending on whether we're able
  // to provide a complete result.
  // This is intended as a safe cast to make Typescript happy, which is why it's not a deep copy.
  const partialResult: BaseCommitmentMechanicsFormData = result;
  let usePartial = false;

  for (const loan of commitment.loans) {
    const topLevelLoan = creditAgreement.loanMechanicsV1?.loans.find(
      (l) => l.key === loan.loanKey,
    );
    if (!topLevelLoan) {
      // TODO: we should get rid of this logic once all of our facilities have correct `loanKey`
      //  references pointing to a valid top-level loan.
      usePartial = true;
      partialResult.products?.push({
        // Workaround to indicate to the user that they should review the loan, since it is lacking
        // key details found on the top level loan. Upon filling in and submitting these details,
        // `setFormValues` will run and the top-level loan will be created.
        name: `${loan.name || 'Placeholder loan'} (needs review)`,
        productType: 'loan',
        pricingOptionKey: '',
        accrualBasis: undefined as any, // This will show as empty in the UI, but user will be required to update it as the field is required
        maturityMonths: null,
      });
    } else {
      result.products.push({
        name: loan.name,
        productType: 'loan',
        pricingOptionKey: topLevelLoan.pricingOptionKey,
        accrualBasis: topLevelLoan.interest.calculation.dayCountConvention,
        maturityMonths: topLevelLoan.maturity.count,
      });
    }
  }

  return usePartial ? partialResult : result;
};

export const getFormValues = (
  commitmentId: string,
  creditAgreement: CreditAgreementFullArtifact,
): BaseCommitmentMechanicsFormData => {
  const commitment = creditAgreement?.loanMechanicsV1?.commitments.find(
    (c) => c.key === commitmentId,
  );

  finleyAssert(commitment, `Cannot find commitment with key ${commitmentId}`);

  return getFormValuesFromCommitment(commitment, creditAgreement);
};
